/**
 * Tipos de Trade Intelligence — preventa.
 *
 * Espejan lo que devuelve `/api/trade`. Se mantienen separados de
 * `import-tracking.types.ts`: ahí vive la operación (órdenes, contenedores),
 * acá la cotización.
 */

import type { QuoteOutcome, QuoteLostReason } from '../utils/quote-outcome';

// ── Catálogos para los selects ───────────────────────────────────────────────

export interface TradeLocation {
  id: string;
  code: string;
  name: string;
  countryCode: string | null;
  type: string;
}

export interface TradeContainerType {
  id: string;
  code: string;
  name: string;
  maxVolumeCbm: number;
  maxWeightKg: number;
  /**
   * Peso máximo de CARGA, ya sin la tara del contenedor vacío.
   *
   * Lo calcula el backend, así la tabla de taras vive en un solo lugar.
   *
   * `maxWeightKg` es el peso **bruto** (carga + contenedor vacío), que es lo que
   * publica la norma ISO 668 y lo que cobra la naviera. Comparar el peso de la
   * mercadería contra ese daría por bueno un contenedor que en la balanza del
   * puerto no pasa.
   */
  maxCargoKg: number;
}

export interface TariffHealth {
  totalRates: number;
  expiredRates: number;
  checkedAt: string;
}

export interface TradeQuoteOptions {
  /** Solo los orígenes que tienen tarifa cargada. */
  origins: TradeLocation[];
  /** Solo los destinos alcanzables con el tarifario actual. */
  destinations: TradeLocation[];
  transitPorts: string[];
  containerTypes: TradeContainerType[];
  incoterms: string[];
  transportModes: { value: string; label: string }[];
  tariffHealth: TariffHealth;
}

export interface TariffStatus {
  totalRates: number;
  validRates: number;
  expiredRates: number;
  expiredBySea: number;
  expiredByLand: number;
  /** Vigentes que vencen dentro de la ventana. Es el número accionable. */
  expiringSoon: number;
  /** Tamaño de esa ventana, en días. Lo define el backend. */
  expiryWarningDays: number;
  nextExpiry: string | null;
  lastExpiry: string | null;
  checkedAt: string;
}

// ── Cotización ───────────────────────────────────────────────────────────────

export interface RateComponent {
  concept: string;
  amount: number;
  payableAt: 'LOCAL' | 'ABROAD';
  /** `true` si la calculó Prixia y no vino del tarifario. */
  isComputed?: boolean;
}

export interface QuotedLeg {
  legType: string;
  transportMode: string;
  origin: string;
  destination: string;
  providerName: string;
  agentName: string | null;
  /** Costo del tarifario. NO es lo que se le cobra al cliente. */
  costAmount: number;
  marginPercent: number;
  marginAmount: number;
  /** Costo + margen. Esto sí es lo que se le cobra al cliente. */
  salePrice: number;
  currency: string;
  components: RateComponent[];
  estimatedDays: number | null;
  validUntil: string | null;
  isExpired: boolean;
  /** De dónde salió la tarifa: VERIFICADO, SIMULADO, o lo que ponga el importador. */
  source: string | null;
  restrictionNotes: string | null;
}

export interface LegAlternative {
  providerName: string;
  costAmount: number;
  /** Por qué no se eligió. `null` = simplemente era más caro. */
  excludedReason: string | null;
}

/** Liquidación aduanera de una opción. Espeja `CustomsTaxResult` del backend. */
export interface CustomsBreakdown {
  fob: number;
  freightReal: number;
  freightComputable: number;
  insurance: number;
  cif: number;
  ga: number;
  ice: number;
  ivaBase: number;
  iva: number;
  totalTaxes: number;
  nationalizedCost: number;
  nationalizedCostPerUnit: number;
  rates: { gaRate: number; iceRate: number; ivaEffectiveRate: number };
  quantity: number;
  via: string;
  ivaExempt: boolean;
  breakdownPercent: Record<string, number>;
  /** Artículos citados. Es lo que hace auditable el cálculo. */
  legalBasis: string[];
}

export interface QuoteOption {
  id: string;
  transportMode: string;
  /** Puerto de tránsito. `null` si el tramo es directo. */
  via: string | null;
  /** Suma de los costos de los tramos. */
  totalCost: number;
  totalMargin: number;
  /** Lo que se le cotiza al cliente. Es el que manda. */
  totalPrice: number;
  currency: string;
  transitDaysMin: number | null;
  transitDaysMax: number | null;
  legs: QuotedLeg[];
  landAlternatives: LegAlternative[];
  isEstimated: boolean;
  hasExpiredRates: boolean;
  warnings: string[];
  customs?: CustomsBreakdown;
}

export interface TariffSuggestion {
  code: string;
  description: string;
  gaRate: number;
}

export interface QuoteCustomsMeta {
  tariffCode: {
    code: string;
    description: string;
    gaRate: number;
    iceRate: number | null;
    ivaExempt: boolean;
    chapterName: string | null;
  } | null;
  /** `true` si la partida no se encontró y se usó el arancel por defecto. */
  isEstimated: boolean;
  gaRateApplied: number;
  insuranceRateApplied: number;
  suggestions: TariffSuggestion[];
}

export interface QuoteResult {
  options: QuoteOption[];
  warnings: string[];
  meta: {
    originName: string | null;
    destinationName: string | null;
    containerTypeCode: string | null;
    quotedAt: string;
    ratesConsidered: number;
  };
  customs: QuoteCustomsMeta | null;
}

export interface QuoteRequest {
  originLocationId: string;
  destinationLocationId: string;
  containerTypeId?: string | null;
  transportMode?: string | null;
  weightKg?: number | null;
  volumeCbm?: number | null;
  hsCode?: string | null;
  fobValue?: number | null;
  insuranceRate?: number | null;
  quantity?: number | null;
}

// ── Extracción de proformas ──────────────────────────────────────────────────

export type ProformaSource = 'EXCEL' | 'PDF' | 'IMAGE';

export interface ProformaLine {
  lineNumber: number;
  description: string;
  quantity: number;
  unitPrice: number;
  unitWeightKg: number | null;
  lengthCm: number | null;
  widthCm: number | null;
  heightCm: number | null;
  /** Partida tal como vino en el documento. */
  hsCodeRaw: string | null;
  /** Partida resuelta contra el arancel boliviano. */
  hsCode: string | null;
  hsDescription: string | null;
  gaRate: number | null;
  /**
   * Id de la partida en el catálogo oficial.
   *
   * Es lo único que hace que la alícuota se resuelva contra el arancel vigente
   * en cada recálculo. Con solo `hsCode` y `gaRate` la línea entra con una tasa
   * escrita, y ningún decreto la alcanza después — que es exactamente lo que
   * pasó con 28 de los 38 ítems ya cargados.
   */
  tariffHeadingId: string | null;
  /** 0 a 1. Por debajo del umbral la línea se marca para revisar. */
  confidence: number;
  issues: string[];
}

export interface ProformaExtraction {
  source: ProformaSource;
  fileName: string;
  supplierName: string | null;
  proformaNumber: string | null;
  proformaDate: string | null;
  currency: string;
  incoterm: string | null;
  declaredTotal: number | null;
  computedTotal: number;
  lines: ProformaLine[];
  warnings: string[];
  needsReview: number;
  usage: { costUsd: number; inputTokens: number; outputTokens: number } | null;
  /**
   * Si la leyó un modelo o un parser.
   *
   * No se deduce del formato: un Excel con columnas irreconocibles también pasa
   * por el modelo, y la persona necesita saberlo para decidir cuánto revisar.
   */
  usedAi: boolean;
}

/** Igual que el backend: `REVIEW_THRESHOLD` de proforma-extract.service. */
export const PROFORMA_REVIEW_THRESHOLD = 0.7;

// ── Guardar la cotización ────────────────────────────────────────────────────

export interface SaveQuoteRequest {
  originLocationId: string;
  destinationLocationId: string;
  containerTypeId?: string | null;
  transportMode?: string | null;
  weightKg?: number | null;
  /** Id de la opción elegida. El servidor recalcula los importes. */
  selectedOptionId: string;
  /** `null` = importación propia: sin margen a tercero ni PDF. */
  customerId?: string | null;
  incoterm?: string | null;
  validForDays?: number;
  notes?: string | null;
  /** De qué cotización sale esta, al recotizar o duplicar. Deja la cadena a la vista. */
  quotedFromId?: string | null;
}

// ── Seguimiento comercial ────────────────────────────────────────

export interface QuoteOutcomeResult {
  id: string;
  orderNumber: string;
  quoteOutcome: QuoteOutcome;
  quoteSentAt: string | null;
  quoteClosedAt: string | null;
  quoteLostReason: QuoteLostReason | null;
  quoteLostNotes: string | null;
  /**
   * Se aceptó pero todavía no es una orden.
   *
   * Son dos decisiones separadas a propósito: se puede registrar que el cliente
   * dijo que sí hoy y convertirla mañana, cuando estén los datos del embarque.
   */
  needsConversion: boolean;
}

export interface QuoteFunnel {
  draft: number;
  sent: number;
  accepted: number;
  lost: number;
  noResponse: number;
  closed: number;
  /** Aceptadas sobre cerradas. `null` mientras no haya cerrado ninguna. */
  winRatePercent: number | null;
  lostByReason: Array<{ reason: QuoteLostReason; label: string; count: number }>;
  /** Cuánta plata espera respuesta: la suma de las enviadas. */
  pipelineValue: number;
  /** Enviadas cuyo precio vence dentro de los próximos 7 días. */
  expiringSoon: number;
  /** Canceladas: quedan FUERA de la tasa de éxito, ni ganadas ni perdidas. */
  cancelled: number;
}

export interface RequoteResult {
  previous: {
    id: string;
    orderNumber: string;
    quotedAt: string | null;
    validUntil: string | null;
    totalPrice: number | null;
    currency: string;
    legs: Array<{
      legType: string;
      origin: string;
      destination: string;
      price: number | null;
    }>;
  };
  /** Lo que hay que repetir al guardar la nueva, listo para mandar. */
  prefill: {
    originLocationId: string;
    destinationLocationId: string;
    containerTypeId: string | null;
    weightKg: number | null;
    customerId: string | null;
    incoterm: string | null;
    notes: string | null;
    quotedFromId: string;
  };
  current: QuoteResult;
  /** `null` si el tarifario ya no cubre la ruta — un resultado tan válido como un precio. */
  comparison: {
    previousTotal: number;
    currentTotal: number;
    difference: number;
    percent: number | null;
    currency: string;
    hasExpiredRates: boolean;
  } | null;
}

export interface SavedQuote {
  id: string;
  orderNumber: string;
  status: string;
  validUntil: string;
  isOwnImport: boolean;
  totalCost: number;
  totalMargin: number;
  totalPrice: number;
  currency: string;
  legs: number;
}

// ── Importación del tarifario ────────────────────────────────────────────────

export interface RejectedRow {
  sheet: string;
  row: number;
  reason: string;
  /** Lo que decía la fila, para ubicarla en el Excel. */
  preview: string;
}

/** Fila que entró pero cuyo desglose no suma el total declarado en el Excel. */
export interface ComponentMismatch {
  sheet: string;
  row: number;
  /** El hueco es fijo por naviera+agente: agrupar por ahí vuelve accionable el reporte. */
  carrier: string;
  agent: string | null;
  declaredTotal: number;
  componentSum: number;
  difference: number;
  preview: string;
}

export interface SheetResult {
  sheet: string;
  read: number;
  imported: number;
  rejected: number;
}

export interface TarifarioImportResult {
  fileName: string;
  sheets: SheetResult[];
  rejected: RejectedRow[];
  mismatches: ComponentMismatch[];
  normalized: Array<{ from: string[]; to: string }>;
  /** Filas que aplicaban a dos equipos y se expandieron. */
  expandedRows: number;
  extractedRestrictions: Array<{ provider: string; requiresCarrier: string }>;
  warnings: string[];
  /** `true` si no se escribió nada: es la previsualización. */
  dryRun: boolean;
}

// ── Aduana ───────────────────────────────────────────────────────────────────

/** Una partida del arancel boliviano, con sus tasas. */
export interface TariffCodeInfo {
  id: string;
  code: string;
  description: string;
  gaRate: number;
  ivaRate: number;
  ivaExempt: boolean;
  hasIce: boolean;
  iceRate: number | null;
  hasIehd: boolean;
  iehdRate: number | null;
  unit: string | null;
  chapter: string | null;
  chapterName: string | null;
  /** Por qué apareció: el código o la descripción. */
  matchedOn?: 'code' | 'description';
}

export interface TariffSearchResponse {
  results: TariffCodeInfo[];
  query: string;
  /** Partidas cercanas cuando el código buscado no existe. */
  suggestions: TariffCodeInfo[];
}

export interface TariffChapter {
  chapter: string;
  name: string;
  count: number;
}

/** La liquidación de tributos, con cada paso visible. */
export interface CustomsSimulation {
  fob: number;
  freightReal: number;
  /** El que integra el CIF. En aéreo es el 25 % del real (Art. 27 LGA). */
  freightComputable: number;
  insurance: number;
  cif: number;
  ga: number;
  ice: number;
  /** CIF + GA + ICE. */
  ivaBase: number;
  iva: number;
  totalTaxes: number;
  nationalizedCost: number;
  nationalizedCostPerUnit: number;
  rates: {
    gaRate: number;
    iceRate: number;
    ivaEffectiveRate: number;
    insuranceRate?: number;
    /** Bs por USD, si se liquidó con tipo de cambio. */
    exchangeRate?: number;
  };
  quantity: number;
  ivaExempt: boolean;
  /** Los mismos importes en bolivianos. Solo si hubo tipo de cambio. */
  bob?: {
    cif: number;
    ga: number;
    ice: number;
    iva: number;
    totalTaxes: number;
    nationalizedCost: number;
    nationalizedCostPerUnit: number;
  };
  /** Los artículos aplicados. */
  legalBasis: string[];
  /** La cuenta paso a paso, con los importes y la norma de cada uno. */
  steps: string[];
  /** `true` si se computó solo una parte del flete aéreo (Art. 27 LGA). */
  airFreightCapApplied: boolean;
}

/** Lo que hay que saber sobre esta liquidación: prohibiciones, topes, faltantes. */
export interface AvisoLiquidacion {
  nivel: 'PROHIBICION' | 'ADVERTENCIA' | 'INFO';
  codigo: string;
  mensaje: string;
  norma?: string;
  codigoPartida?: string;
}

export interface CustomsSimulationResponse {
  tariff: TariffCodeInfo;
  /** `true` si el seguro se estimó con la tasa en vez de informarse. */
  insuranceWasEstimated: boolean;
  result: CustomsSimulation;
  avisos: AvisoLiquidacion[];
  /** El último TC cargado del tenant, con su fecha y fuente. `null` si no hay. */
  tcSugerido: {
    valor: number;
    fecha: string;
    fuente: string;
    desactualizado: boolean;
  } | null;
}

// ── Tarifas editables ────────────────────────────────────────────────────────

export interface EditableRate {
  id: string;
  legType: string;
  transportMode: string;
  origin: string;
  destination: string;
  providerName: string;
  agentName: string | null;
  requiresCarrierName: string | null;
  equipment: string | null;
  amount: number | null;
  currency: string;
  validFrom: string;
  validUntil: string | null;
  isExpired: boolean;
  estimatedDays: number | null;
  maxWeightKg: number | null;
  notes: string | null;
  components: Array<{ concept: string; amount: number }>;
  /**
   * `true` si es una tarifa que ya fue reemplazada por una carga posterior del
   * Excel. Se apila debajo de las vigentes en la misma tabla, y **no se edita**:
   * es lo que se cobró en ese momento.
   */
  esHistorica?: boolean;
  /** Cuándo la reemplazaron. Solo en las históricas. */
  reemplazadaEl?: string;
  /** El archivo que la reemplazó. Solo en las históricas. */
  archivo?: string;
  /** Modalidad del servicio ("Con devolución"). Solo viene en las históricas. */
  serviceType?: string | null;
}

export interface RatePatch {
  amount?: number;
  validUntil?: string | null;
  estimatedDays?: number | null;
  notes?: string | null;
}

/**
 * Una carga del tarifario, en el historial.
 *
 * Cada Excel que se sube reemplaza al anterior —el archivo es una foto completa—
 * así que cada carga es el momento en que un conjunto de precios dejó de regir.
 */
export interface CargaDelHistorial {
  id: string;
  fileName: string;
  uploadedAt: string;
  /** `null` si esa persona ya no está en la empresa. */
  uploadedBy: string | null;
  legTypes: string[];
  ratesImported: number;
  ratesReplaced: number;
}

/**
 * Una tarifa congelada tal como estaba antes de que la pisaran.
 *
 * Los nombres vienen como texto y no como referencias: si después renombran al
 * proveedor o borran la ruta, el histórico se sigue leyendo igual.
 */
export interface FotoDeTarifa {
  id: string;
  origin: string;
  destination: string;
  providerName: string;
  containerCode: string | null;
  serviceType: string | null;
  legType: string;
  amount: number | string;
  currency: string;
  validFrom: string;
  validUntil: string | null;
  components?: Array<{ concept: string; amount: number; payableAt: string }> | null;
}
